import styled, { css } from 'styled-components';
import colours from 'theme/atoms/colours';
import typography from 'theme/atoms/typography';

export const Wrapper = styled('div')`
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 100%;
`;

export const TriggerButton = styled('button')<{
  $open: boolean;
  $fullWidth?: boolean;
  $openUp?: boolean;
  $maxWidth?: number | null;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  box-sizing: border-box;
  border-radius: 30px;
  height: 50px;
  padding: 0 18px;
  cursor: pointer;
  width: ${({ $fullWidth, $maxWidth }) => {
    if ($maxWidth !== undefined) return '100%';
    return $fullWidth ? '100%' : '240px';
  }};
  ${({ $maxWidth }) =>
    $maxWidth !== undefined &&
    ($maxWidth === null
      ? css`
          max-width: none;
        `
      : css`
          max-width: ${$maxWidth}px;
        `)};

  ${typography.p2}
  color: ${colours.neutral[900]};
  text-align: left;

  border: 2px solid rgba(234, 234, 235, 0.9);
  background-color: transparent;

  ${({ $open }) =>
    $open &&
    `
    border-color: rgba(234, 234, 235, 0.9);
  `}

  &:hover {
    border-color: ${colours.neutral[300]};
  }

  &:active {
    border-color: ${colours.neutral[400]};
  }

  /* Avoid showing focus ring on mouse click */
  &:focus {
    outline: none;
  }

  /* Keep an accessible focus ring for keyboard navigation */
  &:focus-visible {
    outline: 2px solid ${colours.primary[500]};
    outline-offset: 2px;
  }
`;

export const Chevron = styled('span')<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${colours.neutral[900]};
  transition: transform 0.2s ease;

  svg {
    stroke-width: 2;
  }

  ${({ $open }) =>
    $open &&
    `
    transform: rotate(180deg);
  `}
`;

export const Popover = styled('div')<{
  $alignRight?: boolean;
  $fullWidth?: boolean;
  $openUp?: boolean;
  $maxWidth?: number | null;
}>`
  position: absolute;
  left: ${({ $alignRight, $fullWidth, $maxWidth }) => {
    if ($maxWidth !== undefined) return '0';
    if ($fullWidth) return '0';
    if ($alignRight) return 'auto';
    return '0';
  }};
  right: ${({ $alignRight, $fullWidth, $maxWidth }) => {
    if ($maxWidth !== undefined) return 'auto';
    if ($fullWidth) return '0';
    if ($alignRight) return '0';
    return 'auto';
  }};
  ${({ $openUp }) =>
    $openUp !== undefined
      ? css`
          top: ${$openUp ? 'auto' : 'calc(100% + 10px)'};
          bottom: ${$openUp ? 'calc(100% + 10px)' : 'auto'};
        `
      : css`
          bottom: calc(100% + 10px);
        `}
  ${({ $fullWidth, $maxWidth }) => {
    if ($maxWidth !== undefined) {
      return $maxWidth === null
        ? css`
            width: 100%;
            min-width: 0;
            max-width: none;
          `
        : css`
            width: 100%;
            min-width: 0;
            max-width: ${$maxWidth}px;
          `;
    }
    return $fullWidth
      ? css`
          width: auto;
          min-width: 0;
          max-width: none;
        `
      : css`
          width: 260px;
          min-width: 260px;
          max-width: 260px;
        `;
  }}
  max-height: 335px;
  padding: 0;
  border-radius: 35px;
  border: 2px solid rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  background: white;
  box-shadow: 0 50px 70px -35px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 1300;
`;

export const List = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-height: 335px;
  overflow-y: auto;
  padding: 10px;
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch;
`;

export const Option = styled('button')<{
  $selected: boolean;
  $compact?: boolean;
}>`
  border: 0;
  width: 100%;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 50px;
  flex-shrink: 0;
  padding: ${({ $compact }) => ($compact ? '8px 15px 8px 8px' : '15px')};
  border-radius: 30px;
  box-sizing: border-box;

  ${typography.p2}

  ${({ $selected }) =>
    $selected
      ? `
    background: ${colours.neutral[900]};
    color: white;
  `
      : `
    background: transparent;
    color: ${colours.neutral[900]};

    &:hover {
      background: ${colours.neutral[100]};
    }
  `}

  &:active {
    background: ${({ $selected }) =>
      $selected ? colours.neutral[900] : colours.neutral[200]};
  }

  /* Avoid showing focus ring on mouse click */
  &:focus {
    outline: none;
  }

  /* Keep an accessible focus ring for keyboard navigation */
  &:focus-visible {
    outline: 2px solid ${colours.primary[500]};
    outline-offset: 2px;
  }
`;

export const OptionContent = styled('span')`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

export const OptionDescription = styled('span')`
  ${typography.label}
  color: ${colours.neutral[500]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const OptionCheck = styled('span')<{
  $selected?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 20px;
  background-color: white;
  flex-shrink: 0;

  svg {
    width: 12px;
    height: 12px;
    color: ${colours.neutral[900]};
  }

  ${({ $selected }) =>
    $selected === false &&
    css`
      background-color: transparent;
      border: 2px solid ${colours.neutral[300]};
    `}
`;
